// src/components/MailAdmin.tsx
// Panel de gestión de mailboxes por org — ticket 064.
// Cada org_admin puede crear y eliminar cuentas de correo sin intervención de devops.
// Design system: Orion glass-morphism (o-card, o-btn-ghost, --acc1/2/3).

import React, { useEffect, useState, useCallback } from 'react';
import {
  listMailboxes, createMailbox, deleteMailbox,
  type Mailbox, type MailboxCreated, type MailboxListResponse,
} from '@/lib/api';
import {
  Loader, Plus, Copy, Check, RefreshCw, Trash2,
  AtSign, Inbox, Eye, EyeOff, KeyRound, AlertCircle,
  Wifi, Server, CheckCircle2, XCircle,
} from 'lucide-react';

/* ──────────────────────────────────────────────────────────────
   Helpers
   ────────────────────────────────────────────────────────────── */

const IMAP_PORT = 993;
const SMTP_PORT = 465;

/** Genera una contraseña segura de 16 chars en el cliente */
function generatePassword(): string {
  const charset = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*';
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => charset[b % charset.length])
    .join('');
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-white/50 hover:text-white/90 hover:bg-white/10 transition-colors"
    >
      {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
      {label && <span className={copied ? 'text-emerald-400' : ''}>{copied ? 'Copiado' : label}</span>}
    </button>
  );
}

function StatusBadge({ status }: { status: Mailbox['status'] }) {
  if (status === 'active') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
      <CheckCircle2 size={11} /> Activa
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-red-500/15 text-red-400 border border-red-500/25">
      <XCircle size={11} /> Suspendida
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────
   One-time credentials reveal (shown right after creation)
   ────────────────────────────────────────────────────────────── */

function CreatedCredentials({ mailbox, onDismiss }: {
  mailbox: MailboxCreated;
  onDismiss: () => void;
}) {
  const [showPass, setShowPass] = useState(false);
  const rows = [
    { label: 'Email', value: mailbox.email },
    { label: 'Contraseña', value: mailbox.password, secret: true },
    { label: 'Servidor IMAP', value: mailbox.imap_host },
    { label: 'Puerto IMAP', value: String(mailbox.imap_port) },
    { label: 'Seguridad IMAP', value: 'SSL/TLS' },
    { label: 'Servidor SMTP', value: mailbox.smtp_host },
    { label: 'Puerto SMTP', value: String(mailbox.smtp_port) },
    { label: 'Seguridad SMTP', value: 'SSL/TLS' },
  ];

  return (
    <div className="o-card border border-emerald-500/30 mb-4">
      <div className="flex items-start gap-3 mb-3">
        <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">Cuenta creada — guarda estas credenciales ahora</p>
          <p className="text-xs text-amber-400 mt-0.5">
            La contraseña sólo se muestra <strong>una vez</strong>. No se puede recuperar después.
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        {rows.map(r => (
          <div key={r.label} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
            <span className="text-xs text-white/40 w-32 shrink-0">{r.label}</span>
            <span className="text-xs text-white/90 font-mono flex-1 truncate">
              {r.secret && !showPass ? '•'.repeat(16) : r.value}
            </span>
            <div className="flex items-center gap-1">
              {r.secret && (
                <button
                  onClick={() => setShowPass(v => !v)}
                  className="p-1 text-white/40 hover:text-white/80"
                >
                  {showPass ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
              )}
              <CopyButton value={r.value} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex justify-end">
        <button
          onClick={onDismiss}
          className="text-sm px-4 py-1.5 rounded-lg bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30 font-medium transition-colors"
        >
          Ya guardé las credenciales
        </button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Main component
   ────────────────────────────────────────────────────────────── */

export function MailAdmin({ addNotification }: { addNotification?: (n: any) => void }) {
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [retryOnAuth, setRetryOnAuth] = useState(false);

  // Creation form
  const [showForm, setShowForm] = useState(false);
  const [localPart, setLocalPart] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [creating, setCreating] = useState(false);

  // One-time reveal
  const [justCreated, setJustCreated] = useState<MailboxCreated | null>(null);
  const [showServerHosts, setShowServerHosts] = useState(false);

  const notify = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    if (addNotification) addNotification({ type, message });
  }, [addNotification]);

  // Derive org domain from first mailbox or from org_id fallback
  const orgDomain = mailboxes[0]?.domain ?? '';

  const load = useCallback(async () => {
    setLoading(true);
    setRetryOnAuth(false);
    try {
      const res = await listMailboxes();
      setMailboxes(res.mailboxes || []);
    } catch (e: any) {
      if (e?.code === 'NO_TOKEN') { setRetryOnAuth(true); }
      else notify('error', e.message);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!retryOnAuth) return;
    const h = () => load();
    window.addEventListener('auth:token-ready', h);
    return () => window.removeEventListener('auth:token-ready', h);
  }, [retryOnAuth, load]);

  const openForm = () => {
    setLocalPart('');
    setPassword(generatePassword());
    setShowPass(false);
    setShowForm(true);
  };

  const handleCreate = async () => {
    const lp = localPart.trim().toLowerCase();
    if (!lp || !password) return;
    if (password.length < 12) {
      notify('error', 'La contraseña debe tener al menos 12 caracteres.');
      return;
    }
    setCreating(true);
    try {
      const created = await createMailbox({ local_part: lp, password });
      setJustCreated(created);
      setShowServerHosts(false);
      setShowForm(false);
      setLocalPart('');
      notify('success', `Cuenta ${created.email} creada`);
      await load();
    } catch (e: any) {
      notify('error', e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (mb: Mailbox) => {
    if (!confirm(`¿Eliminar la cuenta ${mb.email}? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteMailbox(mb.email);
      notify('success', `${mb.email} eliminada`);
      await load();
    } catch (e: any) {
      notify('error', e.message);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Inbox size={22} className="text-[var(--acc1)]" />
          <div>
            <h1 className="text-lg font-semibold text-white">Correo</h1>
            <p className="text-xs text-white/40">Cuentas de correo de tu organización</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="o-btn-ghost p-2 rounded-lg">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={openForm}
            className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-[var(--acc1)] text-white font-medium hover:opacity-90 transition-opacity"
          >
            <Plus size={14} /> Nueva cuenta
          </button>
        </div>
      </div>

      {/* One-time credentials reveal */}
      {justCreated && (
        <CreatedCredentials
          mailbox={justCreated}
          onDismiss={() => setJustCreated(null)}
        />
      )}

      {/* Creation form */}
      {showForm && (
        <div className="o-card border border-[rgba(var(--acc1-rgb),.3)] space-y-4">
          <p className="text-sm font-semibold text-white/90">Nueva cuenta de correo</p>

          {/* Email input */}
          <div>
            <label className="text-xs text-white/50 mb-1 block">Dirección de correo</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center bg-white/5 border border-white/10 rounded-lg overflow-hidden focus-within:border-[var(--acc1)] transition-colors">
                <AtSign size={14} className="text-white/30 ml-3 shrink-0" />
                <input
                  value={localPart}
                  onChange={e => setLocalPart(e.target.value.toLowerCase().replace(/[^a-z0-9._+-]/g, ''))}
                  placeholder="hola"
                  className="flex-1 bg-transparent px-2 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none"
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  autoFocus
                />
                {orgDomain && (
                  <span className="px-3 py-2 text-sm text-white/40 border-l border-white/10 bg-white/5 shrink-0">
                    @{orgDomain}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Password input */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-white/50">Contraseña</label>
              <button
                type="button"
                onClick={() => setPassword(generatePassword())}
                className="text-xs text-[var(--acc2)] hover:opacity-80 flex items-center gap-1"
              >
                <KeyRound size={11} /> Generar nueva
              </button>
            </div>
            <div className="flex items-center bg-white/5 border border-white/10 rounded-lg overflow-hidden focus-within:border-[var(--acc1)] transition-colors">
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="flex-1 bg-transparent px-3 py-2 text-sm text-white font-mono placeholder:text-white/30 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                className="px-3 py-2 text-white/40 hover:text-white/80"
              >
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <CopyButton value={password} />
            </div>
            {password.length > 0 && password.length < 12 && (
              <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                <AlertCircle size={11} /> Mínimo 12 caracteres
              </p>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="o-btn-ghost text-sm px-4 py-2">Cancelar</button>
            <button
              onClick={handleCreate}
              disabled={creating || !localPart.trim() || password.length < 12}
              className="flex items-center gap-2 text-sm px-5 py-2 rounded-lg bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40"
            >
              {creating ? <Loader size={13} className="animate-spin" /> : <Plus size={13} />}
              Crear cuenta
            </button>
          </div>
        </div>
      )}

      {/* IMAP / SMTP server info card */}
      <div className="o-card flex flex-wrap gap-x-8 gap-y-2 text-xs">
        <div className="flex items-center gap-2 text-white/50">
          <Wifi size={13} className="text-[var(--acc2)]" />
          <span>IMAP</span>
          <span className="font-mono text-white/80">
            {justCreated?.imap_host
              ? (showServerHosts ? `${justCreated.imap_host}:${justCreated.imap_port ?? IMAP_PORT} SSL/TLS` : `\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022:${justCreated.imap_port ?? IMAP_PORT} SSL/TLS`)
              : 'Se muestra al crear una cuenta'}
          </span>
          {justCreated?.imap_host && (
            <button
              type="button"
              onClick={() => setShowServerHosts(v => !v)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-white/50 hover:text-white/90 hover:bg-white/10 transition-colors"
            >
              {showServerHosts ? <EyeOff size={12} /> : <Eye size={12} />}
              {showServerHosts ? 'Ocultar' : 'Mostrar'}
            </button>
          )}
          {justCreated?.imap_host && showServerHosts && (
            <CopyButton value={`${justCreated.imap_host}:${justCreated.imap_port ?? IMAP_PORT}`} />
          )}
        </div>
        <div className="flex items-center gap-2 text-white/50">
          <Server size={13} className="text-[var(--acc2)]" />
          <span>SMTP</span>
          <span className="font-mono text-white/80">
            {justCreated?.smtp_host
              ? (showServerHosts ? `${justCreated.smtp_host}:${justCreated.smtp_port ?? SMTP_PORT} SSL/TLS` : `\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022:${justCreated.smtp_port ?? SMTP_PORT} SSL/TLS`)
              : 'Se muestra al crear una cuenta'}
          </span>
          {justCreated?.smtp_host && showServerHosts && (
            <CopyButton value={`${justCreated.smtp_host}:${justCreated.smtp_port ?? SMTP_PORT}`} />
          )}
        </div>
      </div>

      {/* Mailboxes list */}
      <div className="o-card">
        {loading
          ? <div className="flex justify-center py-12"><Loader size={20} className="animate-spin text-white/40" /></div>
          : mailboxes.length === 0
            ? (
              <div className="text-center py-14 text-white/20">
                <AtSign size={32} className="mx-auto mb-3 opacity-30" />
                <p>Sin cuentas de correo</p>
                <p className="text-xs mt-1">Crea la primera cuenta usando el botón de arriba.</p>
              </div>
            )
            : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-white/10">
                    <th className="pb-2 text-xs font-medium text-white/40">Cuenta</th>
                    <th className="pb-2 text-xs font-medium text-white/40">Estado</th>
                    <th className="pb-2 text-xs font-medium text-white/40 hidden sm:table-cell">Creada</th>
                    <th className="pb-2 text-xs font-medium text-white/40 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {mailboxes.map(mb => (
                    <tr key={mb.email} className="group">
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <AtSign size={13} className="text-white/30 shrink-0" />
                          <span className="font-mono text-white/90">{mb.email}</span>
                          <CopyButton value={mb.email} />
                        </div>
                      </td>
                      <td className="py-3"><StatusBadge status={mb.status} /></td>
                      <td className="py-3 text-white/40 hidden sm:table-cell">
                        {new Date(mb.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 text-right">
                        <button
                          onClick={() => handleDelete(mb)}
                          className="p-1.5 rounded text-white/30 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                          title="Eliminar cuenta"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        }
      </div>
    </div>
  );
}
